import { ReactNode } from 'react';

import reportTypes, { SelectableReportTypes } from './reportTypes';
import {
  kriDescription,
  pepSanctionHitsDescription,
  portfolioScreeningDescription,
  ruleThresholdVariationDescription,
  ruleStatisticsDescription,
  ruleOverviewDescription,
  requestOverviewDescription,
  extendedTransactionsDescription,
  pingOverviewDescription,
  caseOverviewDescription,
  caseStatusHistoryDescription,
  groupDurationDescription,
  entityTransactionsDescription,
  entityOverviewDescription,
  businessUsageDescription,
  accountFirstInitiatorDescription,
} from './reportMetadataDescriptions';

export interface OutputColumn {
  id: string;
  name: string;
  description: string;
  isDynamic?: boolean;
  defaultOff?: boolean;
  isRequired?: boolean;
}

export interface OutputRow {
  id: string;
  name: string;
  description: string;
}

export interface OutputSheet {
  id: string;
  name: string;
  columns: OutputColumn[];
  rows: OutputRow[];
}

export interface ReportMeta {
  detailedDescription: ReactNode;
  outputSheets: OutputSheet[];
}

const kriOutputRows: OutputRow[] = [
  {
    id: 'transactionRequests',
    name: 'Transaction requests',
    description: 'Number of transaction requests processed during the period.',
  },
  {
    id: 'individualKycRequests',
    name: 'Individual KYC requests',
    description: 'Number of individual KYC requests processed during the period.',
  },
  {
    id: 'businessKycRequests',
    name: 'Business KYC requests',
    description: 'Number of business KYC requests processed during the period.',
  },
  {
    id: 'ongoingRequests',
    name: 'Ongoing requests',
    description: 'Number of ongoing monitoring requests during the period.',
  },
  {
    id: 'totalPings',
    name: 'Total Pings',
    description: 'Total number of pings generated during the period.',
  },
  {
    id: 'uniqueIndividualsWithPings',
    name: 'Unique individuals with Pings',
    description: 'Unique individuals with at least one ping generated during the period.',
  },
  {
    id: 'uniqueBusinessesWithPings',
    name: 'Unique businesses with Pings',
    description: 'Unique businesses with at least one ping generated during the period.',
  },
  {
    id: 'nonClosedStart',
    name: 'Non-Closed, start',
    description: 'Number of non-closed cases in the system at the start of the period.',
  },
  {
    id: 'createdCases',
    name: 'Created',
    description: 'Number of cases generated during the period.',
  },
  {
    id: 'closedCases',
    name: 'Closed',
    description: 'Number of cases closed during the period.',
  },
  {
    id: 'nonClosedEnd',
    name: 'Non-Closed, end',
    description: 'Number of non-closed cases in the system at the end of the period.',
  },
  {
    id: 'individualsBlocked',
    name: 'No of individuals - blocked',
    description: 'Number of blocked individuals at the end of the period.',
  },
  {
    id: 'individualsNotBlocked',
    name: 'No of individuals - not blocked',
    description: 'Number of non-blocked individuals at the end of the period.',
  },
  {
    id: 'individualsNotInRequests',
    name: 'No of individuals - not part of any request',
    description: 'Number of individuals that are not referenced in any request at the end of the period.',
  },
  {
    id: 'businessesBlocked',
    name: 'No of businesses - blocked',
    description: 'Number of blocked businesses at the end of the period.',
  },
  {
    id: 'businessesNotBlocked',
    name: 'No of businesses - not blocked',
    description: 'Number of non-blocked businesses at the end of the period.',
  },
  {
    id: 'businessesNotInRequests',
    name: 'No of businesses - not part of any request',
    description: 'Number of businesses that are not referenced in any request at the end of the period.',
  },
  {
    id: 'accountsBlocked',
    name: 'No of accounts - blocked',
    description: 'Number of blocked accounts at the end of the period.',
  },
  {
    id: 'accountsNotBlocked',
    name: 'No of accounts - not blocked',
    description: 'Number of non-blocked accounts at the end of the period.',
  },
  {
    id: 'accountsNotInRequests',
    name: 'No of accounts - not part of any request',
    description: 'Number of accounts that are not referenced in any request at the end of the period.',
  },
  {
    id: 'productsBlocked',
    name: 'No of products - blocked',
    description: 'Number of blocked products at the end of the period.',
  },
  {
    id: 'productsNotBlocked',
    name: 'No of products - not blocked',
    description: 'Number of non-blocked products at the end of the period.',
  },
  {
    id: 'productsNotInRequests',
    name: 'No of products - not part of any request',
    description: 'Number of products that are not referenced in any request at the end of the period.',
  },
  {
    id: 'pepPings',
    name: 'PEP Pings',
    description: 'Number of PEP pings generated during the period.',
  },
  {
    id: 'pepPending',
    name: 'PEP Pending',
    description: 'Pings among PEP Pings that are pending at report generation time.',
  },
  {
    id: 'pepConfirmed',
    name: 'PEP Confirmed',
    description: 'Pings among PEP Pings that are confirmed at report generation time.',
  },
  {
    id: 'sanctionPings',
    name: 'Sanction Pings',
    description: 'Number of sanction list pings generated during the period.',
  },
  {
    id: 'sanctionPending',
    name: 'Sanction Pending',
    description: 'Pings among Sanction Pings that are pending at report generation time.',
  },
  {
    id: 'sanctionConfirmed',
    name: 'Sanction Confirmed',
    description: 'Pings among Sanction Pings that are confirmed at report generation time.',
  },
  {
    id: 'str',
    name: 'STR',
    description: 'Number of Suspicious Transaction Reports generated during the period.',
  },
  {
    id: 'sar',
    name: 'SAR',
    description: 'Number of Suspicious Activity Reports generated during the period.',
  },
  {
    id: 'Individuals <riskClass>',
    name: 'Individuals <riskClass>',
    description: 'Number of individuals in the risk class <riskClass> at the end of the period (one row per risk class in system).',
  },
  {
    id: 'Businesses <riskClass>',
    name: 'Businesses <riskClass>',
    description: 'Number of businesses in the risk class <riskClass> at the end of the period (one row per risk class in system).',
  },
  {
    id: 'Accounts <riskClass>',
    name: 'Accounts <riskClass>',
    description: 'Number of accounts in the risk class <riskClass> at the end of the period (one row per risk class in system).',
  },
  {
    id: 'Products <riskClass>',
    name: 'Products <riskClass>',
    description: 'Number of products in the risk class <riskClass> at the end of the period (one row per risk class in system).',
  },
];

export const reportMetadata: Record<SelectableReportTypes, ReportMeta> = {
  [reportTypes.KRI_REPORT]: {
    detailedDescription: kriDescription,
    outputSheets: [
      {
        id: 'kriMaster',
        name: 'kriMaster',
        columns: [
          {
            id: 'period',
            name: '<from> - <to>',
            description: 'Dynamic period column generated for each configured period, including both From and To dates.',
            isDynamic: true,
          },
        ],
        rows: kriOutputRows,
      },
    ],
  },
  [reportTypes.PEP_SANCTION_HITS_THRESHOLD_VARIATION]: {
    detailedDescription: pepSanctionHitsDescription,
    outputSheets: [
      {
        id: 'pepHits',
        name: 'PEP Hits',
        columns: [
          {
            id: 'individual_id',
            name: 'individual_id',
            description: 'The unique identifier of the individual checked',
          },
          {
            id: 'birthdate',
            name: 'birthdate',
            description: 'The birthdate of the individual checked',
          },
          {
            id: 'individual_createdat',
            name: 'individual_createdat',
            description: 'The date the individual was created in the system',
            isRequired: true,
          },
          {
            id: 'pep_hit_70',
            name: 'pep_hit_70',
            description: '1 if the match probability of the PEP record is ≥ 70, else 0',
          },
          {
            id: 'pep_hit_75',
            name: 'pep_hit_75',
            description: '1 if the match probability of the PEP record is ≥ 75, else 0',
          },
          {
            id: 'pep_hit_80',
            name: 'pep_hit_80',
            description: '1 if the match probability of the PEP record is ≥ 80, else 0',
          },
          {
            id: 'pep_hit_85',
            name: 'pep_hit_85',
            description: '1 if the match probability of the PEP record is ≥ 85, else 0',
          },
          {
            id: 'pep_hit_90',
            name: 'pep_hit_90',
            description: '1 if the match probability of the PEP record is ≥ 90, else 0',
          },
          {
            id: 'pep_hit_95',
            name: 'pep_hit_95',
            description: '1 if the match probability of the PEP record is ≥ 95, else 0',
          },
          {
            id: 'pep_hit_100',
            name: 'pep_hit_100',
            description: '1 if the match probability of the PEP record is exactly 100, else 0',
          },
        ],
        rows: [
          {
            id: 'one-row-per-individual',
            name: 'One row per individual',
            description: 'Each row represents one individual created within the selected date range. The individual is included regardless of whether they have a PEP hit.',
          },
        ],
      },
      {
        id: 'sanctionHits',
        name: 'Sanction Hits',
        columns: [
          {
            id: 'individual_id',
            name: 'individual_id',
            description: 'The unique identifier of the individual checked',
          },
          {
            id: 'birthdate',
            name: 'birthdate',
            description: 'The birthdate of the individual checked',
          },
          {
            id: 'individual_createdat',
            name: 'individual_createdat',
            description: 'The date the individual was created in the system',
            isRequired: true,
          },
          {
            id: 'sanction_hit_70',
            name: 'sanction_hit_70',
            description: '1 if the match probability of the sanction record is ≥ 70, else 0',
          },
          {
            id: 'sanction_hit_75',
            name: 'sanction_hit_75',
            description: '1 if the match probability of the sanction record is ≥ 75, else 0',
          },
          {
            id: 'sanction_hit_80',
            name: 'sanction_hit_80',
            description: '1 if the match probability of the sanction record is ≥ 80, else 0',
          },
          {
            id: 'sanction_hit_85',
            name: 'sanction_hit_85',
            description: '1 if the match probability of the sanction record is ≥ 85, else 0',
          },
          {
            id: 'sanction_hit_90',
            name: 'sanction_hit_90',
            description: '1 if the match probability of the sanction record is ≥ 90, else 0',
          },
          {
            id: 'sanction_hit_95',
            name: 'sanction_hit_95',
            description: '1 if the match probability of the sanction record is ≥ 95, else 0',
          },
          {
            id: 'sanction_hit_100',
            name: 'sanction_hit_100',
            description: '1 if the match probability of the sanction record is exactly 100, else 0',
          },
        ],
        rows: [
          {
            id: 'one-row-per-individual',
            name: 'One row per individual',
            description: 'Each row represents one individual created within the selected date range. The individual is included regardless of whether they have a sanction hit.',
          },
        ],
      },
      {
        id: 'adverseMediaHits',
        name: 'Adverse Media Hits',
        columns: [
          {
            id: 'individual_id',
            name: 'individual_id',
            description: 'The unique identifier of the individual checked',
          },
          {
            id: 'birthdate',
            name: 'birthdate',
            description: 'The birthdate of the individual checked',
          },
          {
            id: 'individual_createdat',
            name: 'individual_createdat',
            description: 'The date the individual was created in the system',
            isRequired: true,
          },
          {
            id: 'adversemedia_hit_70',
            name: 'adversemedia_hit_70',
            description: '1 if the match probability of the adverse media record is ≥ 70, else 0',
          },
          {
            id: 'adversemedia_hit_75',
            name: 'adversemedia_hit_75',
            description: '1 if the match probability of the adverse media record is ≥ 75, else 0',
          },
          {
            id: 'adversemedia_hit_80',
            name: 'adversemedia_hit_80',
            description: '1 if the match probability of the adverse media record is ≥ 80, else 0',
          },
          {
            id: 'adversemedia_hit_85',
            name: 'adversemedia_hit_85',
            description: '1 if the match probability of the adverse media record is ≥ 85, else 0',
          },
          {
            id: 'adversemedia_hit_90',
            name: 'adversemedia_hit_90',
            description: '1 if the match probability of the adverse media record is ≥ 90, else 0',
          },
          {
            id: 'adversemedia_hit_95',
            name: 'adversemedia_hit_95',
            description: '1 if the match probability of the adverse media record is ≥ 95, else 0',
          },
          {
            id: 'adversemedia_hit_100',
            name: 'adversemedia_hit_100',
            description: '1 if the match probability of the adverse media record is exactly 100, else 0',
          },
        ],
        rows: [
          {
            id: 'one-row-per-individual',
            name: 'One row per individual',
            description: 'Each row represents one individual created within the selected date range. The individual is included regardless of whether they have an adverse media hit.',
          },
        ],
      },
    ],
  },

  [reportTypes.PORTFOLIO_SCREENING_REPORT]: {
    detailedDescription: portfolioScreeningDescription,
    outputSheets: [
      {
        id: 'screeningPEP',
        name: 'screeningPEP',
        columns: [
          {
            id: 'entityid',
            name: 'entityid',
            description: 'Entity ID.',
            isRequired: true,
          },
          { id: 'entity_type', name: 'entity_type', description: 'Entity type (Individual or Business).' },
          { id: 'createdat', name: 'createdat', description: 'Entity creation datetime.' },
          { id: 'identifier_individual', name: 'identifier_individual', description: 'Individual identifier (for individual entities).' },
          { id: 'identifier_business', name: 'identifier_business', description: 'Business identifier (for business entities).' },
          { id: 'group_assignment', name: 'group_assignment', description: 'Names of groups assigned to the entity.' },
          { id: 'pep_screened_at_1', name: 'pep_screened_at_1', description: 'Datetime of most recent PEP collection, if available.' },
          { id: 'pep_screened_at_1_highest_match', name: 'pep_screened_at_1_highest_match', description: 'Highest PEP match percentage for most recent collection.' },
          { id: 'pep_screened_at_2', name: 'pep_screened_at_2', description: 'Datetime of second most recent PEP collection, if available.' },
          { id: 'pep_screened_at_2_highest_match', name: 'pep_screened_at_2_highest_match', description: 'Highest PEP match percentage for second most recent collection.' },
          { id: 'pep_screened_at_3', name: 'pep_screened_at_3', description: 'Datetime of third most recent PEP collection, if available.' },
          { id: 'pep_screened_at_3_highest_match', name: 'pep_screened_at_3_highest_match', description: 'Highest PEP match percentage for third most recent collection.' },
          { id: 'pep_screened_at_4', name: 'pep_screened_at_4', description: 'Datetime of fourth most recent PEP collection, if available.' },
          { id: 'pep_screened_at_4_highest_match', name: 'pep_screened_at_4_highest_match', description: 'Highest PEP match percentage for fourth most recent collection.' },
          { id: 'pep_screened_at_5', name: 'pep_screened_at_5', description: 'Datetime of fifth most recent PEP collection, if available.' },
          { id: 'pep_screened_at_5_highest_match', name: 'pep_screened_at_5_highest_match', description: 'Highest PEP match percentage for fifth most recent collection.' },
        ],
        rows: [
          {
            id: 'one-row-per-entity',
            name: 'One row per entity',
            description: 'Each row represents one entity included in the report scope.',
          },
        ],
      },
      {
        id: 'screeningSanction',
        name: 'screeningSanction',
        columns: [
          {
            id: 'entityid',
            name: 'entityid',
            description: 'Entity ID.',
            isRequired: true,
          },
          { id: 'entity_type', name: 'entity_type', description: 'Entity type (Individual or Business).' },
          { id: 'createdat', name: 'createdat', description: 'Entity creation datetime.' },
          { id: 'identifier_individual', name: 'identifier_individual', description: 'Individual identifier (for individual entities).' },
          { id: 'identifier_business', name: 'identifier_business', description: 'Business identifier (for business entities).' },
          { id: 'group_assignment', name: 'group_assignment', description: 'Names of groups assigned to the entity.' },
          { id: 'sanction_screened_at_1', name: 'sanction_screened_at_1', description: 'Datetime of most recent sanction collection, if available.' },
          { id: 'sanction_screened_at_1_highest_match', name: 'sanction_screened_at_1_highest_match', description: 'Highest sanction match percentage for most recent collection.' },
          { id: 'sanction_screened_at_2', name: 'sanction_screened_at_2', description: 'Datetime of second most recent sanction collection, if available.' },
          { id: 'sanction_screened_at_2_highest_match', name: 'sanction_screened_at_2_highest_match', description: 'Highest sanction match percentage for second most recent collection.' },
          { id: 'sanction_screened_at_3', name: 'sanction_screened_at_3', description: 'Datetime of third most recent sanction collection, if available.' },
          { id: 'sanction_screened_at_3_highest_match', name: 'sanction_screened_at_3_highest_match', description: 'Highest sanction match percentage for third most recent collection.' },
          { id: 'sanction_screened_at_4', name: 'sanction_screened_at_4', description: 'Datetime of fourth most recent sanction collection, if available.' },
          { id: 'sanction_screened_at_4_highest_match', name: 'sanction_screened_at_4_highest_match', description: 'Highest sanction match percentage for fourth most recent collection.' },
          { id: 'sanction_screened_at_5', name: 'sanction_screened_at_5', description: 'Datetime of fifth most recent sanction collection, if available.' },
          { id: 'sanction_screened_at_5_highest_match', name: 'sanction_screened_at_5_highest_match', description: 'Highest sanction match percentage for fifth most recent collection.' },
        ],
        rows: [
          {
            id: 'one-row-per-entity',
            name: 'One row per entity',
            description: 'Each row represents one entity included in the report scope.',
          },
        ],
      },
      {
        id: 'screeningAdverseMedia',
        name: 'screeningAdverseMedia',
        columns: [
          {
            id: 'entityid',
            name: 'entityid',
            description: 'Entity ID.',
            isRequired: true,
          },
          { id: 'entity_type', name: 'entity_type', description: 'Entity type (Individual or Business).' },
          { id: 'createdat', name: 'createdat', description: 'Entity creation datetime.' },
          { id: 'identifier_individual', name: 'identifier_individual', description: 'Individual identifier (for individual entities).' },
          { id: 'identifier_business', name: 'identifier_business', description: 'Business identifier (for business entities).' },
          { id: 'group_assignment', name: 'group_assignment', description: 'Names of groups assigned to the entity.' },
          { id: 'adversemedia_screened_at_1', name: 'adversemedia_screened_at_1', description: 'Datetime of most recent adverse media collection, if available.' },
          { id: 'adversemedia_screened_at_1_highest_match', name: 'adversemedia_screened_at_1_highest_match', description: 'Highest adverse media match percentage for most recent collection.' },
          { id: 'adversemedia_screened_at_2', name: 'adversemedia_screened_at_2', description: 'Datetime of second most recent adverse media collection, if available.' },
          { id: 'adversemedia_screened_at_2_highest_match', name: 'adversemedia_screened_at_2_highest_match', description: 'Highest adverse media match percentage for second most recent collection.' },
          { id: 'adversemedia_screened_at_3', name: 'adversemedia_screened_at_3', description: 'Datetime of third most recent adverse media collection, if available.' },
          { id: 'adversemedia_screened_at_3_highest_match', name: 'adversemedia_screened_at_3_highest_match', description: 'Highest adverse media match percentage for third most recent collection.' },
          { id: 'adversemedia_screened_at_4', name: 'adversemedia_screened_at_4', description: 'Datetime of fourth most recent adverse media collection, if available.' },
          { id: 'adversemedia_screened_at_4_highest_match', name: 'adversemedia_screened_at_4_highest_match', description: 'Highest adverse media match percentage for fourth most recent collection.' },
          { id: 'adversemedia_screened_at_5', name: 'adversemedia_screened_at_5', description: 'Datetime of fifth most recent adverse media collection, if available.' },
          { id: 'adversemedia_screened_at_5_highest_match', name: 'adversemedia_screened_at_5_highest_match', description: 'Highest adverse media match percentage for fifth most recent collection.' },
        ],
        rows: [
          {
            id: 'one-row-per-entity',
            name: 'One row per entity',
            description: 'Each row represents one entity included in the report scope.',
          },
        ],
      },
      {
        id: 'latestRuleExecution',
        name: 'latestRuleExecution',
        columns: [
          {
            id: 'entityid',
            name: 'entityid',
            description: 'Entity ID.',
            isRequired: true,
          },
          { id: 'entity_type', name: 'entity_type', description: 'Entity type (Individual or Business).' },
          { id: 'createdat', name: 'createdat', description: 'Entity creation datetime.' },
          { id: 'identifier_individual', name: 'identifier_individual', description: 'Individual identifier (for individual entities).' },
          { id: 'identifier_business', name: 'identifier_business', description: 'Business identifier (for business entities).' },
          { id: 'group_assignment', name: 'group_assignment', description: 'Names of groups assigned to the entity.' },
          {
            id: 'latest_rule_execution_<origination_rule_id>',
            name: 'latest_rule_execution_<origination_rule_id>',
            description: 'Latest execution timestamp for each selected origination rule ID.',
            isDynamic: true,
          },
        ],
        rows: [
          {
            id: 'one-row-per-entity',
            name: 'One row per entity',
            description: 'Each row represents one entity included in the report scope.',
          },
        ],
      },
    ],
  },

  [reportTypes.RULE_THRESHOLD_VARIATION]: {
    detailedDescription: ruleThresholdVariationDescription,
    outputSheets: [
      {
        id: 'thresholdVariation',
        name: 'Threshold Variation',
        columns: [
          {
            id: 'origination_rule_id',
            name: 'origination_rule_id',
            description: 'The origination rule ID that groups all versions of the same rule',
            isRequired: true,
          },
          {
            id: 'ruleids',
            name: 'ruleids',
            description: 'A list of rule IDs belonging to this origination rule ID',
          },
          {
            id: 'triggertype',
            name: 'triggertype',
            description: 'The type of rule trigger: request (per-transaction) or ongoing (periodic)',
          },
          {
            id: 'ruletype',
            name: 'ruletype',
            description: 'The generic type of the rule (e.g. AmountThreshold, OutgoingVolumeRollingSum)',
          },
          {
            id: 'rulename',
            name: 'rulename',
            description: 'The custom name of the rule as configured in the system',
          },
          {
            id: 'period_start',
            name: 'period_start',
            description: 'The effective start of the analysis period (max of input "from" date and rule start date)',
          },
          {
            id: 'period_end',
            name: 'period_end',
            description: 'The effective end of the analysis period (min of input "to" date and rule end date)',
          },
          {
            id: 'ruleparamthresholdprio',
            name: 'ruleparamthresholdprio',
            description: 'The threshold priority setting of the rule (KYC values vs. rule default)',
          },
          {
            id: 'ruleparamthresholdeur',
            name: 'ruleparamthresholdeur',
            description: 'The configured EUR threshold of the rule',
          },
          {
            id: 'create_ping',
            name: 'create_ping',
            description: 'Whether the rule is configured to create pings when triggered',
          },
          {
            id: 'ruleparamperiodunit',
            name: 'ruleparamperiodunit',
            description: 'The time period unit used by the rule (e.g. days, hours)',
          },
          {
            id: 'ruleparamperiodnb',
            name: 'ruleparamperiodnb',
            description: 'The number of period units used by the rule',
          },
          {
            id: 'check_initiator',
            name: 'check_initiator',
            description: 'Whether the rule evaluates the initiating party of a transaction',
          },
          {
            id: 'check_recipient',
            name: 'check_recipient',
            description: 'Whether the rule evaluates the recipient party of a transaction',
          },
          {
            id: 'check_processor',
            name: 'check_processor',
            description: 'Whether the rule evaluates the processor party of a transaction',
          },
          {
            id: 'check_subprocessor',
            name: 'check_subprocessor',
            description: 'Whether the rule evaluates the subprocessor party of a transaction',
          },
          {
            id: 'check_entity',
            name: 'check_entity',
            description: 'Whether the rule evaluates the entity role',
          },
          {
            id: 'nb_pings',
            name: 'nb_pings',
            description: 'Actual number of pings created during the period with the current threshold',
          },
          {
            id: 'nb_pings_pending',
            name: 'nb_pings_pending',
            description: 'Number of pings with status "pending" created during the period',
          },
          {
            id: 'nb_pings_resolved',
            name: 'nb_pings_resolved',
            description: 'Number of pings with status "resolved" created during the period',
          },
          {
            id: 'nb_pings_ignored',
            name: 'nb_pings_ignored',
            description: 'Number of pings with status "ignored" created during the period',
          },
          {
            id: 'nb_pings_confirmed',
            name: 'nb_pings_confirmed',
            description: 'Number of pings with status "confirmed" created during the period',
          },
          {
            id: 'backtest_c_<factor>',
            name: 'backtest_c_<factor>',
            description: 'Estimated number of pings if the threshold is changed by the given factor. One column per factor provided in thresholdChangesToBacktest. The suffix is the factor multiplied by 100 as an integer (e.g. backtest_c_75 for 0.75×, backtest_c_125 for 1.25×).',
            isDynamic: true,
          },
        ],
        rows: [
          {
            id: 'one-row-per-rule',
            name: 'One row per rule',
            description: 'Each row represents one origination rule ID (i.e. one logical rule across all its versions) that was active during the selected period.',
          },
        ],
      },
    ],
  },

  [reportTypes.RULE_STATISTICS]: {
    detailedDescription: ruleStatisticsDescription,
    outputSheets: [
      {
        id: 'ruleStatistics',
        name: 'Rule Statistics',
        columns: [
          {
            id: 'origination_rule_id',
            name: 'origination_rule_id',
            description: 'The origination rule ID that groups all versions of the same rule',
            isRequired: true,
          },
          {
            id: 'ruleids',
            name: 'ruleids',
            description: 'List of rule IDs considered as "the same rule" by the report',
          },
          {
            id: 'startdate',
            name: 'startdate',
            description: 'Effective start of the analysis period for this rule (max of report date and rule start date)',
          },
          {
            id: 'enddate',
            name: 'enddate',
            description: 'Effective end of the analysis period for this rule (min of report date and rule end date)',
          },
          {
            id: 'min_version_number',
            name: 'min_version_number',
            description: 'The first rule version number in which the rule was active',
          },
          {
            id: 'max_version_number',
            name: 'max_version_number',
            description: 'The last rule version number in which the rule was active',
          },
          {
            id: 'ruletype',
            name: 'ruletype',
            description: 'The generic type name of the rule',
          },
          {
            id: 'rulename',
            name: 'rulename',
            description: 'The custom user-defined name of the rule',
          },
          {
            id: 'triggertype',
            name: 'triggertype',
            description: 'The trigger mechanism: request (per-transaction) or ongoing (periodic)',
          },
          {
            id: 'is_test_version',
            name: 'is_test_version',
            description: 'Whether this is a test version of the rule',
          },
          {
            id: 'ruleparamthresholdprio',
            name: 'ruleparamthresholdprio',
            description: 'The threshold priority setting (KYC-based or rule default)',
          },
          {
            id: 'ruleparamthresholdeur',
            name: 'ruleparamthresholdeur',
            description: 'The EUR threshold of the rule',
          },
          {
            id: 'ruleparamthresholdsek',
            name: 'ruleparamthresholdsek',
            description: 'The SEK threshold of the rule',
          },
          {
            id: 'ruleparamthresholdcountcommon',
            name: 'ruleparamthresholdcountcommon',
            description: 'The count-based default threshold of the rule',
          },
          {
            id: 'ruleparamperiodunit',
            name: 'ruleparamperiodunit',
            description: 'The time period unit used by the rule',
          },
          {
            id: 'ruleparamperiodnb',
            name: 'ruleparamperiodnb',
            description: 'The number of period units used by the rule',
          },
          {
            id: 'total_nb_transactions',
            name: 'total_nb_transactions',
            description: 'Total number of transactions in the rule period with the specified currency',
          },
          {
            id: 'total_transaction_amount',
            name: 'total_transaction_amount',
            description: 'Total transaction amount in the rule period with the specified currency',
          },
          {
            id: 'total_nb_distinct_initiators',
            name: 'total_nb_distinct_initiators',
            description: 'Number of distinct initiators in transactions during the rule period',
          },
          {
            id: 'total_nb_distinct_recipients',
            name: 'total_nb_distinct_recipients',
            description: 'Number of distinct recipients in transactions during the rule period',
          },
          { id: 'nb_transactions_rule', name: 'nb_transactions_rule', description: 'Transactions that passed the rule\'s entity role and group filters' },
          {
            id: 'transaction_amount_rule',
            name: 'transaction_amount_rule',
            description: 'Total amount of the transactions counted in nb_transactions_rule',
          },
          {
            id: 'nb_distinct_initiators_rule',
            name: 'nb_distinct_initiators_rule',
            description: 'Distinct initiators in transactions that passed rule filters',
          },
          {
            id: 'nb_distinct_recipients_rule',
            name: 'nb_distinct_recipients_rule',
            description: 'Distinct recipients in transactions that passed rule filters',
          },
          {
            id: 'nb_triggers_rule',
            name: 'nb_triggers_rule',
            description: 'Number of times the rule was triggered (error pings excluded)',
          },
          {
            id: 'nb_pings_created_rule',
            name: 'nb_pings_created_rule',
            description: 'Number of pings created by the rule (error pings excluded)',
          },
          {
            id: 'createsping',
            name: 'createsping',
            description: 'Whether the rule is configured to create pings when triggered',
          },
          {
            id: 'nb_pings',
            name: 'nb_pings',
            description: 'Total number of pings created by the rule in the period',
          },
          {
            id: 'nb_pings_pending',
            name: 'nb_pings_pending',
            description: 'Number of pings in "pending" status',
          },
          {
            id: 'nb_pings_resolved',
            name: 'nb_pings_resolved',
            description: 'Number of pings in "resolved" status',
          },
          {
            id: 'nb_pings_ignored',
            name: 'nb_pings_ignored',
            description: 'Number of pings in "ignored" status',
          },
          {
            id: 'nb_pings_confirmed',
            name: 'nb_pings_confirmed',
            description: 'Number of pings in "confirmed" status',
          },
          {
            id: 'nb_initiators_with_pings',
            name: 'nb_initiators_with_pings',
            description: 'Distinct initiators that had at least one ping in the period',
          },
          {
            id: 'nb_recipients_with_pings',
            name: 'nb_recipients_with_pings',
            description: 'Distinct recipients that had at least one ping in the period',
          },
          {
            id: 'hitvolume',
            name: 'hitvolume',
            description: 'Total transaction volume that contributed to rule triggers',
          },
          {
            id: 'hitthreshold',
            name: 'hitthreshold',
            description: 'The threshold value that was breached to cause triggers',
          },
          {
            id: 'hitperiodamount',
            name: 'hitperiodamount',
            description: 'The period amount parameter at the time of the hit',
          },
          {
            id: 'hitperiodunit',
            name: 'hitperiodunit',
            description: 'The period unit parameter at the time of the hit',
          },
          {
            id: 'hitcurrency',
            name: 'hitcurrency',
            description: 'The currency in which the hit was evaluated',
          },
          {
            id: 'hitcashflow',
            name: 'hitcashflow',
            description: 'Total cash flow volume for rule triggers during the period',
          },
          {
            id: 'rule_tags',
            name: 'rule_tags',
            description: 'Comma-separated tag names assigned to the rule',
          },
        ],
        rows: [
          {
            id: 'one-row-per-rule',
            name: 'One row per rule',
            description: 'Each row represents one origination rule ID (i.e. a logical rule across all its versions) that was active during at least part of the selected from–to period.',
          },
        ],
      },
    ],
  },

  [reportTypes.RULE_OVERVIEW]: {
    detailedDescription: ruleOverviewDescription,
    outputSheets: [
      {
        id: 'rulesList',
        name: 'Rules List',
        columns: [
          {
            id: 'ruleid',
            name: 'ruleid',
            description: 'The unique identifier of the rule version',
          },
          {
            id: 'origination_rule_id',
            name: 'origination_rule_id',
            description: 'The origination rule ID grouping all versions of the same rule',
            isRequired: true,
          },
          {
            id: 'correlationid',
            name: 'correlationid',
            description: 'The correlation ID linking related rule versions',
          },
          {
            id: 'createdat',
            name: 'createdat',
            description: 'The date when this rule version was created',
            isRequired: true,
          },
          {
            id: 'ruletype',
            name: 'ruletype',
            description: 'The generic type of the rule (e.g. AmountThreshold)',
          },
          {
            id: 'rulename',
            name: 'rulename',
            description: 'The custom user-defined name of the rule',
          },
          {
            id: 'triggertype',
            name: 'triggertype',
            description: 'Whether this is a request or ongoing trigger rule',
          },
          {
            id: 'ruleparamthresholdprio',
            name: 'ruleparamthresholdprio',
            description: 'The threshold priority parameter (KYC-based or default)',
          },
          {
            id: 'ruleparamthresholdeur',
            name: 'ruleparamthresholdeur',
            description: 'The EUR threshold configured for the rule',
          },
          {
            id: 'ruleparamthresholdsek',
            name: 'ruleparamthresholdsek',
            description: 'The SEK threshold configured for the rule',
          },
          {
            id: 'ruleparamthresholddkk',
            name: 'ruleparamthresholddkk',
            description: 'The DKK threshold configured for the rule',
          },
          {
            id: 'ruleparamperiodunit',
            name: 'ruleparamperiodunit',
            description: 'The time unit for the rolling window parameter',
          },
          {
            id: 'ruleparamperiodnb',
            name: 'ruleparamperiodnb',
            description: 'The number of time units for the rolling window',
          },
          {
            id: 'ruleparamthresholdcountcommon',
            name: 'ruleparamthresholdcountcommon',
            description: 'The count-based threshold parameter of the rule',
          },
          {
            id: 'ruleparamformdeviationeur',
            name: 'ruleparamformdeviationeur',
            description: 'The form deviation in EUR threshold parameter',
          },
          {
            id: 'createdping',
            name: 'createdping',
            description: 'Whether the rule creates pings when triggered',
          },
          {
            id: 'check_initiator',
            name: 'check_initiator',
            description: 'Whether the rule evaluates the initiating entity',
          },
          {
            id: 'check_recipient',
            name: 'check_recipient',
            description: 'Whether the rule evaluates the recipient entity',
          },
          {
            id: 'check_entity',
            name: 'check_entity',
            description: 'Whether the rule evaluates the entity role',
          },
          {
            id: 'check_processor',
            name: 'check_processor',
            description: 'Whether the rule evaluates the processor entity',
          },
          {
            id: 'check_subprocessor',
            name: 'check_subprocessor',
            description: 'Whether the rule evaluates the subprocessor entity',
          },
          {
            id: 'initiator_groupfiltering',
            name: 'initiator_groupfiltering',
            description: 'Group filtering configuration applied to initiators',
          },
          {
            id: 'recipient_groupfiltering',
            name: 'recipient_groupfiltering',
            description: 'Group filtering configuration applied to recipients',
          },
          {
            id: 'entity_groupfiltering',
            name: 'entity_groupfiltering',
            description: 'Group filtering configuration applied to entities',
          },
          {
            id: 'processor_groupfiltering',
            name: 'processor_groupfiltering',
            description: 'Group filtering configuration applied to processors',
          },
          {
            id: 'subprocessor_groupfiltering',
            name: 'subprocessor_groupfiltering',
            description: 'Group filtering configuration applied to subprocessors',
          },
          {
            id: 'initiator_groupsassignment',
            name: 'initiator_groupsassignment',
            description: 'Whether the rule assigns initiators to groups when triggered',
          },
          {
            id: 'recipient_groupsassignment',
            name: 'recipient_groupsassignment',
            description: 'Whether the rule assigns recipients to groups when triggered',
          },
          {
            id: 'entity_groupsassignment',
            name: 'entity_groupsassignment',
            description: 'Whether the rule assigns entities to groups when triggered',
          },
          {
            id: 'processor_groupsassignment',
            name: 'processor_groupsassignment',
            description: 'Whether the rule assigns processors to groups when triggered',
          },
          {
            id: 'subprocessor_groupsassignment',
            name: 'subprocessor_groupsassignment',
            description: 'Whether the rule assigns subprocessors to groups when triggered',
          },
          {
            id: 'process',
            name: 'process',
            description: 'The process name associated with the rule',
          },
          {
            id: 'score',
            name: 'score',
            description: 'The risk score parameter of the rule',
          },
          {
            id: 'isautoconfirmed',
            name: 'isautoconfirmed',
            description: 'Whether pings generated by this rule are automatically confirmed',
          },
          {
            id: 'recommendation',
            name: 'recommendation',
            description: 'The recommendation parameter configured for the rule',
          },
          {
            id: 'rule_tags',
            name: 'rule_tags',
            description: 'Tags assigned to the rule',
          },
          {
            id: 'rule_startdate',
            name: 'rule_startdate',
            description: 'The date this rule version became active',
          },
          {
            id: 'rule_enddate',
            name: 'rule_enddate',
            description: 'The date this rule version was deactivated (NULL if still active)',
          },
          {
            id: 'is_test_version',
            name: 'is_test_version',
            description: 'Whether this is a test version of the rule',
          },
          {
            id: 'max_version_number',
            name: 'max_version_number',
            description: 'The highest version number associated with this origination rule ID',
          },
          {
            id: 'min_version_number',
            name: 'min_version_number',
            description: 'The lowest version number associated with this origination rule ID',
          },
        ],
        rows: [
          {
            id: 'one-row-per-rule-version',
            name: 'One row per rule version',
            description: 'Each row represents one rule version within the selected version range. Multiple rows may share the same origination_rule_id if the rule was updated across versions.',
          },
        ],
      },
    ],
  },
  [reportTypes.REQUEST_OVERVIEW]: {
    detailedDescription: requestOverviewDescription,
    outputSheets: [
      {
        id: 'requestList',
        name: 'Request List',
        columns: [
          {
            id: 'requestid',
            name: 'requestid',
            description: 'The unique identifier of the request',
          },
          {
            id: 'customid',
            name: 'customid',
            description: 'The client-assigned custom identifier for the request',
          },
          {
            id: 'createdat',
            name: 'createdat',
            description: 'Timestamp when the request was created in the system',
          },
          {
            id: 'requesttype',
            name: 'requesttype',
            description: 'The type of request (e.g. transaction, entity check)',
          },
          {
            id: 'process',
            name: 'process',
            description: 'The process ID associated with the request',
          },
          {
            id: 'requestamount',
            name: 'requestamount',
            description: 'The transaction amount (applicable for transaction requests)',
          },
          {
            id: 'currency',
            name: 'currency',
            description: 'The currency of the transaction (applicable for transaction requests)',
          },
          {
            id: 'status',
            name: 'status',
            description: 'The current processing status of the request',
          },
          {
            id: 'signal',
            name: 'signal',
            description: 'The first signal ID returned by the recommendation engine for this request',
          },
          {
            id: 'initiatorid',
            name: 'initiatorid',
            description: 'The unique identifier of the initiating entity',
          },
          {
            id: 'initiatortype',
            name: 'initiatortype',
            description: 'The entity type of the initiator',
          },
          {
            id: 'initiatorip',
            name: 'initiatorip',
            description: 'The IP address from which the initiator submitted the request',
          },
          {
            id: 'recipientid',
            name: 'recipientid',
            description: 'The unique identifier of the recipient entity',
          },
          {
            id: 'recipienttype',
            name: 'recipienttype',
            description: 'The entity type of the recipient',
          },
          {
            id: 'recipientip',
            name: 'recipientip',
            description: 'The IP address of the recipient',
          },
          {
            id: 'processorid',
            name: 'processorid',
            description: 'The unique identifier of the processing entity',
          },
          {
            id: 'processortype',
            name: 'processortype',
            description: 'The entity type of the processor',
          },
          {
            id: 'processorip',
            name: 'processorip',
            description: 'The IP address of the processor',
          },
          {
            id: 'subprocessorid',
            name: 'subprocessorid',
            description: 'The unique identifier of the subprocessing entity',
          },
          {
            id: 'subprocessortype',
            name: 'subprocessortype',
            description: 'The entity type of the subprocessor',
          },
          {
            id: 'subprocessorip',
            name: 'subprocessorip',
            description: 'The IP address of the subprocessor',
          },
          {
            id: 'entityid',
            name: 'entityid',
            description: 'The unique identifier of the main entity associated with the request',
          },
          {
            id: 'entitytype',
            name: 'entitytype',
            description: 'The entity type of the main entity',
          },
          {
            id: 'entityip',
            name: 'entityip',
            description: 'The IP address of the main entity',
          },
          {
            id: 'signals',
            name: 'signals',
            description: 'The full list of signal IDs returned by the recommendation engine',
          },
        ],
        rows: [
          {
            id: 'one-row-per-request',
            name: 'One row per request',
            description: 'Each row represents one request processed by the platform that matches all the specified filter criteria.',
          },
        ],
      },
    ],
  },

  [reportTypes.EXTENDED_TRANSACTIONS]: {
    detailedDescription: extendedTransactionsDescription,
    outputSheets: [
      {
        id: 'extendedTransactions',
        name: 'Extended Transactions',
        columns: [
          {
            id: 'transaction_date_and_time',
            name: 'transaction_date_and_time',
            description: 'When the transaction was created',
            isRequired: true,
          },
          {
            id: 'transaction_id',
            name: 'transaction_id',
            description: 'Unique identifier for the transaction',
          },
          {
            id: 'transaction_direction',
            name: 'transaction_direction',
            description: 'Outgoing when individual is initiator, otherwise incoming',
          },
          {
            id: 'transaction_type',
            name: 'transaction_type',
            description: 'Transaction type (external)',
          },
          {
            id: 'amount',
            name: 'amount',
            description: 'Transaction amount',
          },
          {
            id: 'currency',
            name: 'currency',
            description: 'Transaction currency',
          },
          {
            id: 'initiator',
            name: 'initiator',
            description: 'Initiator name (individual or business)',
          },
          {
            id: 'initiator_account_number',
            name: 'initiator_account_number',
            description: 'IBAN for individuals, blank for businesses',
          },
          {
            id: 'initiator_country',
            name: 'initiator_country',
            description: 'SWE for individual, MALTA for business',
          },
          {
            id: 'recipient',
            name: 'recipient',
            description: 'Recipient name (individual or business)',
          },
          {
            id: 'recipient_account_number',
            name: 'recipient_account_number',
            description: 'IBAN for individuals, blank for businesses',
          },
          {
            id: 'recipient_country',
            name: 'recipient_country',
            description: 'SWE for individual, MALTA for business',
          },
          {
            id: 'transaction_ip_address',
            name: 'transaction_ip_address',
            description: 'Initiator IP when individual is initiator, otherwise recipient IP',
          },
          {
            id: 'transaction_status',
            name: 'transaction_status',
            description: 'Transaction status',
          },
          {
            id: 'generated_ping_date',
            name: 'generated_ping_date',
            description: 'Ping creation time',
          },
          {
            id: 'pingid',
            name: 'pingid',
            description: 'Unique identifier for the ping',
          },
          {
            id: 'istestping',
            name: 'istestping',
            description: '1 if the ping is a test ping, 0 otherwise',
          },
          {
            id: 'clientid',
            name: 'clientid',
            description: 'National ID from initiator when individual is initiator, otherwise from recipient',
          },
          {
            id: 'ping_resolving',
            name: 'ping_resolving',
            description: 'Ping decision time',
          },
          {
            id: 'ping_scenario_name',
            name: 'ping_scenario_name',
            description: 'Rule custom name or rule name',
          },
          {
            id: 'ping_comments',
            name: 'ping_comments',
            description: 'Comments on the ping (format: message|member_id|date)',
          },
          {
            id: 'employee_name',
            name: 'employee_name',
            description: 'Full name of the case assignee',
          },
        ],
        rows: [
          {
            id: 'one-row-per-ping-per-transaction',
            name: 'One row per ping per transaction',
            description: 'Each row represents a transaction that matches the selected filters.',
          },
        ],
      },
    ],
  },

  [reportTypes.PING_OVERVIEW]: {
    detailedDescription: pingOverviewDescription,
    outputSheets: [
      {
        id: 'pingList',
        name: 'Ping List',
        columns: [
          {
            id: 'pingid',
            name: 'pingid',
            description: 'The unique identifier of the ping',
          },
          {
            id: 'requestid',
            name: 'requestid',
            description: 'The unique identifier of the request that triggered the ping',
          },
          {
            id: 'caseid',
            name: 'caseid',
            description: 'The identifier of the case associated with this ping, if any',
          },
          {
            id: 'ping_createdat',
            name: 'ping_createdat',
            description: 'The date and time when the ping was created',
            isRequired: true,
          },
          {
            id: 'description',
            name: 'description',
            description: 'The description text of the ping as generated by the rule',
          },
          {
            id: 'entityid',
            name: 'entityid',
            description: 'The unique identifier of the entity the ping was generated for',
          },
          {
            id: 'entitytype',
            name: 'entitytype',
            description: 'The type of the entity (Individual, Business, Account, etc.)',
          },
          {
            id: 'status',
            name: 'status',
            description: 'The current status of the ping (confirmed, pending, ignored, resolved)',
          },
          {
            id: 'decisiondate',
            name: 'decisiondate',
            description: 'The most recent date the ping status was changed (empty for auto-confirmed)',
          },
          {
            id: 'expirydate',
            name: 'expirydate',
            description: 'The expiry date of the ping',
          },
          {
            id: 'score',
            name: 'score',
            description: 'The risk score associated with the ping',
          },
          {
            id: 'riskstartdate',
            name: 'riskstartdate',
            description: 'The date from which the risk assessment for this ping is valid',
          },
          {
            id: 'riskexpirydate',
            name: 'riskexpirydate',
            description: 'The date on which the risk assessment for this ping expires',
          },
          {
            id: 'is_error_ping',
            name: 'is_error_ping',
            description: 'Whether this is an error ping (1 = error, 0 = normal)',
          },
          {
            id: 'ruleid',
            name: 'ruleid',
            description: 'The unique identifier of the rule version that generated this ping',
          },
          {
            id: 'ruletype',
            name: 'ruletype',
            description: 'The generic type of the rule that generated this ping',
          },
          {
            id: 'triggertype',
            name: 'triggertype',
            description: 'Whether the triggering rule is request-based or ongoing',
          },
          {
            id: 'rulename',
            name: 'rulename',
            description: 'The custom name of the rule that generated this ping',
          },
          {
            id: 'ruleversion_startdate',
            name: 'ruleversion_startdate',
            description: 'The start date of the rule version that generated this ping',
          },
          {
            id: 'ruleversion_enddate',
            name: 'ruleversion_enddate',
            description: 'The end date of the rule version that generated this ping',
          },
          {
            id: 'version_number',
            name: 'version_number',
            description: 'The version number of the rule that generated this ping',
          },
          {
            id: 'origination_rule_id',
            name: 'origination_rule_id',
            description: 'The origination rule ID (stable identifier across rule versions)',
          },
          {
            id: 'ping_tags',
            name: 'ping_tags',
            description: 'Tag names assigned to this ping',
          },
          {
            id: 'rule_tags',
            name: 'rule_tags',
            description: 'Tag names assigned to the rule that generated this ping',
          },
        ],
        rows: [
          {
            id: 'one-row-per-ping',
            name: 'One row per ping',
            description: 'Each row represents one ping created within the selected from–to period that matches all specified filter criteria.',
          },
        ],
      },
      {
        id: 'pingStats',
        name: 'Ping Statistics',
        columns: [
          {
            id: 'origination_rule_id',
            name: 'origination_rule_id',
            description: 'The origination rule ID grouping all pings from the same rule line',
            isRequired: true,
          },
          {
            id: 'ruletype',
            name: 'ruletype',
            description: 'The generic type of the rule',
          },
          {
            id: 'triggertype',
            name: 'triggertype',
            description: 'Whether the rule is request-based or ongoing',
          },
          {
            id: 'rulename',
            name: 'rulename',
            description: 'The custom name of the rule',
          },
          {
            id: 'min_ruleversion_startdate',
            name: 'min_ruleversion_startdate',
            description: 'Start date of the earliest rule version that created pings in the list',
          },
          {
            id: 'max_ruleversion_enddate',
            name: 'max_ruleversion_enddate',
            description: 'End date of the latest rule version that created pings in the list',
          },
          {
            id: 'min_version_number',
            name: 'min_version_number',
            description: 'The earliest version number that generated pings in the list',
          },
          {
            id: 'max_version_number',
            name: 'max_version_number',
            description: 'The latest version number that generated pings in the list',
          },
          {
            id: 'confirmed_count',
            name: 'confirmed_count',
            description: 'Number of confirmed pings generated by this rule',
          },
          {
            id: 'pending_count',
            name: 'pending_count',
            description: 'Number of pending pings generated by this rule',
          },
          {
            id: 'ignored_count',
            name: 'ignored_count',
            description: 'Number of ignored pings generated by this rule',
          },
          {
            id: 'resolved_count',
            name: 'resolved_count',
            description: 'Number of resolved pings generated by this rule',
          },
        ],
        rows: [
          {
            id: 'one-row-per-rule',
            name: 'One row per rule',
            description: 'Each row represents one origination rule group that generated at least one ping in the ping list sheet.',
          },
        ],
      },
    ],
  },

  [reportTypes.CASE_STATUS_HISTORY_REPORT]: {
    detailedDescription: caseStatusHistoryDescription,
    outputSheets: [
      {
        id: 'caseStatusHistory',
        name: 'Case Status History',
        columns: [
          {
            id: 'case_id',
            name: 'case_id',
            description: 'ID of the case from the status history.',
            isRequired: true,
          },
          {
            id: 'status_update_date',
            name: 'status_update_date',
            description: 'Time of the status change (UTC).',
            isRequired: true,
          },
          {
            id: 'status_update_by',
            name: 'status_update_by',
            description: 'Name of the member who made the status change; empty if unavailable.',
          },
          {
            id: 'updaterid',
            name: 'updaterid',
            description: 'ID of the member recorded on the status history entry.',
          },
          {
            id: 'status_updated_to',
            name: 'status_updated_to',
            description: 'Status recorded by this update, not the current case status.',
          },
          {
            id: 'assigned_to',
            name: 'assigned_to',
            description: 'Current assigned member name; empty if no member is available.',
          },
          {
            id: 'case_current_status',
            name: 'case_current_status',
            description: 'Current status of the case.',
          },
          {
            id: 'case_createdat',
            name: 'case_createdat',
            description: 'When the case was created.',
          },
          {
            id: 'case_first_20_entities',
            name: 'case_first_20_entities',
            description: 'JSON list of up to 20 entities (type and ID) related to the case.',
          },
          {
            id: 'case_fiporeport_createdat',
            name: 'case_fiporeport_createdat',
            description: 'Date of creation of the first FIPO report, if any; otherwise NULL.',
          },
          {
            id: 'case_numberofcomments',
            name: 'case_numberofcomments',
            description: 'Total number of comments on the case.',
          },
          {
            id: 'case_tags',
            name: 'case_tags',
            description: 'Comma-separated list of tag IDs of the case.',
          },
          {
            id: 'case_duration_open',
            name: 'case_duration_open',
            description: 'Total duration (hours) the case was in Open status.',
          },
          {
            id: 'case_duration_in_progress',
            name: 'case_duration_in_progress',
            description: 'Total duration (hours) the case was in in-progress status.',
          },
          {
            id: 'case_duration_pending',
            name: 'case_duration_pending',
            description: 'Total duration (hours) the case was in pending status.',
          },
          {
            id: 'case_escalated_to',
            name: 'case_escalated_to',
            description: 'ID of the member the case was last escalated to, if any; otherwise NULL.',
          },
        ],
        rows: [
          {
            id: 'one-row-per-status-update',
            name: 'One row per matching status update',
            description: 'Includes repeated changes on the same case. Additional case columns contain current case details at generation time.',
          },
        ],
      },
    ],
  },

  [reportTypes.CASE_OVERVIEW]: {
    detailedDescription: caseOverviewDescription,
    outputSheets: [
      {
        id: 'caseList',
        name: 'Case List',
        columns: [
          {
            id: 'caseid',
            name: 'caseid',
            description: 'The unique identifier of the case',
          },
          {
            id: 'case_createdat',
            name: 'case_createdat',
            description: 'The date and time the case was created',
            isRequired: true,
          },
          {
            id: 'case_status',
            name: 'case_status',
            description: 'The current status of the case (open, in-progress, pending, closed)',
          },
          {
            id: 'close_date',
            name: 'close_date',
            description: 'The date the case was closed (NULL if still open)',
          },
          {
            id: 'fiporeport_createdat',
            name: 'fiporeport_createdat',
            description: 'Date when the first FIPO (police) report was created for this case, if any',
          },
          {
            id: 'currencies',
            name: 'currencies',
            description: 'Comma-separated list of currencies involved in requests linked to this case',
          },
          {
            id: 'tag_names',
            name: 'tag_names',
            description: 'Comma-separated list of tag names assigned to the case',
          },
          {
            id: 'pingids',
            name: 'pingids',
            description: 'Comma-separated list of ping IDs that triggered or are linked to this case',
          },
          {
            id: 'requestids',
            name: 'requestids',
            description: 'Comma-separated list of request IDs linked to this case',
          },
          {
            id: 'processorids',
            name: 'processorids',
            description: 'Comma-separated list of processor entity IDs linked to this case',
          },
          {
            id: 'processor_business_names',
            name: 'processor_business_names',
            description: 'Business names of processors linked to this case',
          },
          {
            id: 'subprocessorids',
            name: 'subprocessorids',
            description: 'Comma-separated list of sub-processor entity IDs linked to this case',
          },
          {
            id: 'subprocessor_business_names',
            name: 'subprocessor_business_names',
            description: 'Business names of sub-processors linked to this case',
          },
          {
            id: 'recipientids',
            name: 'recipientids',
            description: 'Comma-separated list of recipient entity IDs linked to this case',
          },
          {
            id: 'initiatorids',
            name: 'initiatorids',
            description: 'Comma-separated list of initiator entity IDs linked to this case',
          },
          {
            id: 'triggers',
            name: 'triggers',
            description: 'Comma-separated list of rule names or origination rule IDs that triggered the case',
          },
          {
            id: 'lists_truncated',
            name: 'lists_truncated',
            description: 'Indicates whether any linked ID lists were truncated due to excessive data volume',
          },
          {
            id: 'duration_hours_open',
            name: 'duration_hours_open',
            description: 'Total hours the case spent in "open" status',
          },
          {
            id: 'duration_hours_in_progress',
            name: 'duration_hours_in_progress',
            description: 'Total hours the case spent in "in-progress" status',
          },
          {
            id: 'duration_hours_pending',
            name: 'duration_hours_pending',
            description: 'Total hours the case spent in "pending" status',
          },
          {
            id: 'duration_hours_total',
            name: 'duration_hours_total',
            description: 'Total hours the case spent across all active statuses',
          },
          {
            id: 'numberofcomments',
            name: 'numberofcomments',
            description: 'Total number of analyst comments added to the case',
          },
          {
            id: 'assigned_to',
            name: 'assigned_to',
            description: 'ID of the team member the case is currently assigned to',
          },
          {
            id: 'escalated_to',
            name: 'escalated_to',
            description: 'ID of the team member the case was last escalated to',
          },
          {
            id: 'case_entities',
            name: 'case_entities',
            description: 'Comma-separated list of entity IDs linked to the case',
          },
          {
            id: 'case_entitytypes',
            name: 'case_entitytypes',
            description: 'Comma-separated list of entity types for entities linked to the case',
          },
          {
            id: 'entityrole',
            name: 'entityrole',
            description: 'Whether the entity is the "subject" of the case or only "associated" through a linked request (present when entityIds filter is used)',
          },
        ],
        rows: [
          {
            id: 'one-row-per-case',
            name: 'One row per case',
            description: 'Each row represents one AML case that was open at any point during the selected period. A maximum of 700,000 of the most recently created cases are included.',
          },
        ],
      },
      {
        id: 'tagsSummary',
        name: 'Tags Summary',
        columns: [
          {
            id: 'tagsSummary',
            name: 'tagsSummary',
            description: 'The tag category used to group cases in this row',
          },
          {
            id: 'totalCases',
            name: 'Total Cases',
            description: 'Total number of cases in this tag category',
          },
          {
            id: 'open',
            name: 'Open',
            description: 'Number of cases in "Open" status',
          },
          {
            id: 'inProgress',
            name: 'In-progress',
            description: 'Number of cases in "In-progress" status',
          },
          {
            id: 'pending',
            name: 'Pending',
            description: 'Number of cases in "Pending" status',
          },
          {
            id: 'closed',
            name: 'Closed',
            description: 'Number of cases in "Closed" status',
          },
          {
            id: 'closedWithin7Days',
            name: 'Closed within 7 days',
            description: 'Cases closed within 7 days of creation',
          },
          {
            id: 'closedWithin8To14Days',
            name: 'Closed within 8-14 days',
            description: 'Cases closed between 8 and 14 days of creation',
          },
          {
            id: 'closedWithin15To30Days',
            name: 'Closed within 15-30 days',
            description: 'Cases closed between 15 and 30 days of creation',
          },
          {
            id: 'closedOver30Days',
            name: 'Closed over 30 days',
            description: 'Cases closed after more than 30 days',
          },
          {
            id: 'avgDaysToClose',
            name: 'Avg. Days to Close',
            description: 'Average number of days to close a case in this category (closed cases only)',
          },
          {
            id: 'highestDaysToClose',
            name: 'Highest Days to Close',
            description: 'Maximum days to close a case in this category',
          },
          {
            id: 'avgDaysInOpen',
            name: 'Avg. Days in Open',
            description: 'Average days cases spent in "Open" status',
          },
          {
            id: 'avgDaysInInProgress',
            name: 'Avg. Days in In Progress',
            description: 'Average days cases spent in "In-progress" status',
          },
          {
            id: 'avgDaysInPending',
            name: 'Avg. Days in Pending',
            description: 'Average days cases spent in "Pending" status',
          },
          {
            id: 'casesReportedToPolice',
            name: 'Cases reported to Police',
            description: 'Number of cases that have an associated FIPO (police) report',
          },
        ],
        rows: [
          {
            id: 'aggregate-rows-plus-one-row-per-tag',
            name: 'Aggregate rows plus one row per tag',
            description: 'The sheet includes one row for each unique tag assigned to matching cases, plus three aggregate rows: one for all cases (Total), one for tagged cases, and one for untagged cases.',
          },
        ],
      },
      {
        id: 'assignedSummary',
        name: 'Assigned Summary',
        columns: [
          {
            id: 'assignedSummary',
            name: 'assignedSummary',
            description: 'The analyst assignment category for this row',
          },
          {
            id: 'totalCases',
            name: 'Total Cases',
            description: 'Total number of cases assigned to this category',
          },
          {
            id: 'open',
            name: 'Open',
            description: 'Number of cases in "Open" status',
          },
          {
            id: 'inProgress',
            name: 'In-progress',
            description: 'Number of cases in "In-progress" status',
          },
          {
            id: 'pending',
            name: 'Pending',
            description: 'Number of cases in "Pending" status',
          },
          {
            id: 'closed',
            name: 'Closed',
            description: 'Number of cases in "Closed" status',
          },
          {
            id: 'closedWithin7Days',
            name: 'Closed within 7 days',
            description: 'Cases closed within 7 days of creation',
          },
          {
            id: 'closedWithin8To14Days',
            name: 'Closed within 8-14 days',
            description: 'Cases closed between 8 and 14 days of creation',
          },
          {
            id: 'closedWithin15To30Days',
            name: 'Closed within 15-30 days',
            description: 'Cases closed between 15 and 30 days of creation',
          },
          {
            id: 'closedOver30Days',
            name: 'Closed over 30 days',
            description: 'Cases closed after more than 30 days',
          },
          {
            id: 'avgDaysToClose',
            name: 'Avg. Days to Close',
            description: 'Average days to close a case for this analyst group',
          },
          {
            id: 'highestDaysToClose',
            name: 'Highest Days to Close',
            description: 'Maximum days to close any case in this analyst group',
          },
          {
            id: 'avgDaysInOpen',
            name: 'Avg. Days in Open',
            description: 'Average time cases spent in "Open" status for this analyst group',
          },
          {
            id: 'avgDaysInInProgress',
            name: 'Avg. Days in In Progress',
            description: 'Average time cases spent in "In-progress" for this analyst group',
          },
          {
            id: 'avgDaysInPending',
            name: 'Avg. Days in Pending',
            description: 'Average time cases spent in "Pending" for this analyst group',
          },
          {
            id: 'casesReportedToPolice',
            name: 'Cases reported to Police',
            description: 'Number of cases with FIPO reports in this analyst group',
          },
        ],
        rows: [
          {
            id: 'one-row-per-analyst-assignment-category',
            name: 'One row per analyst assignment category',
            description: 'Each row represents one analyst or analyst group that was assigned at least one matching case. An "Unassigned" row is included for cases with no analyst assignment.',
          },
        ],
      },
    ],
  },

  [reportTypes.GROUP_DURATION]: {
    detailedDescription: groupDurationDescription,
    outputSheets: [
      {
        id: 'GroupDuration',
        name: 'Group Duration',
        columns: [
          {
            id: 'entityid',
            name: 'entityid',
            description: 'The unique identifier of the entity',
          },
          {
            id: 'entitytype',
            name: 'entitytype',
            description: 'The type of the entity',
          },
          {
            id: '<group>_first_assigned_date',
            name: '<group>_first_assigned_date',
            description: 'Timestamp of the first assignment to this group. Column name uses the cleaned group name (lowercased, non-alpha chars replaced with _).',
            isDynamic: true,
          },
          {
            id: '<group>_last_assigned_date',
            name: '<group>_last_assigned_date',
            description: 'Timestamp of the last assignment to this group.',
            isDynamic: true,
          },
          {
            id: '<group>_first_unassigned_date',
            name: '<group>_first_unassigned_date',
            description: 'Timestamp of the first unassignment from this group.',
            isDynamic: true,
          },
          {
            id: '<group>_last_unassigned_date',
            name: '<group>_last_unassigned_date',
            description: 'Timestamp of the last unassignment from this group.',
            isDynamic: true,
          },
          {
            id: '<group>_total_nb_assigns',
            name: '<group>_total_nb_assigns',
            description: 'Total number of assignments to this group.',
            isDynamic: true,
          },
          {
            id: '<group>_total_nb_un_assigns',
            name: '<group>_total_nb_un_assigns',
            description: 'Total number of unassignments from this group.',
            isDynamic: true,
          },
          {
            id: '<group>_is_assigned_at_report_generation',
            name: '<group>_is_assigned_at_report_generation',
            description: '1 if the entity is currently assigned to the group at report generation time, 0 otherwise.',
            isDynamic: true,
          },
          {
            id: '<group>_total_time',
            name: '<group>_total_time',
            description: 'Total number of days spent in this group as of report generation.',
            isDynamic: true,
          },
          {
            id: '<group>_average_time',
            name: '<group>_average_time',
            description: 'Average number of days per assignment to this group.',
            isDynamic: true,
          },
        ],
        rows: [
          {
            id: 'one-row-per-entity',
            name: 'One row per entity',
            description: 'One row per entity that has any assignment or unassignment events in the selected groups.',
          },
        ],
      },
      {
        id: 'GroupDurationSummary',
        name: 'Group Duration Summary',
        columns: [
          {
            id: 'metric',
            name: 'metric',
            description: 'Summary metric name',
            isRequired: true,
          },
          {
            id: '<group>',
            name: '<group>',
            description: 'Metric value for the group. Column name uses the cleaned group name (lowercased, non-alpha chars replaced with _).',
            isDynamic: true,
          },
        ],
        rows: [
          {
            id: 'nb_assigns',
            name: 'nb_assigns',
            description: 'Total number of assignments across all entities in this group.',
          },
          {
            id: 'nb_entities_assigned',
            name: 'nb_entities_assigned',
            description: 'Count of unique entities that have been assigned to this group.',
          },
          {
            id: 'nb_entities_assigned_multiple_times',
            name: 'nb_entities_assigned_multiple_times',
            description: 'Count of unique entities that have been assigned to this group more than once.',
          },
          {
            id: 'average_nb_days_in_group',
            name: 'average_nb_days_in_group',
            description: 'Average number of days per assignment to this group.',
          },
          {
            id: 'max_days_in_group',
            name: 'max_days_in_group',
            description: 'Maximum number of days spent in a single assignment to this group.',
          },
          {
            id: 'min_days_in_group',
            name: 'min_days_in_group',
            description: 'Minimum number of days spent in a single assignment to this group.',
          },
        ],
      },
    ],
  },

  [reportTypes.ENTITY_TRANSACTIONS]: {
    detailedDescription: entityTransactionsDescription,
    outputSheets: [
      {
        id: 'entityTransactions',
        name: 'Entity Transactions',
        columns: [
          {
            id: 'entityid',
            name: 'entityid',
            description: 'The unique identifier of the entity',
          },
          {
            id: 'entitytype',
            name: 'entitytype',
            description: 'The type of entity (Individual, Business, Account, or Product)',
          },
          {
            id: 'nationalid',
            name: 'nationalid',
            description: 'National ID of the entity (present for Individual entity type)',
          },
          {
            id: 'nationalid_country',
            name: 'nationalid_country',
            description: 'Country of the national ID (present for Individual entity type)',
          },
          {
            id: 'iban',
            name: 'iban',
            description: 'IBAN of the entity (present for Account entity type)',
          },
          {
            id: 'businessname',
            name: 'businessname',
            description: 'Business name of the entity (present for Business entity type)',
          },
          {
            id: 'product_description',
            name: 'product_description',
            description: 'Product description (present for Product entity type)',
          },
          {
            id: 'groupnames',
            name: 'groupnames',
            description: 'Names of groups the entity belongs to (present if groupIds filter is provided)',
          },
          {
            id: 'groupids',
            name: 'groupids',
            description: 'IDs of groups the entity belongs to (present if groupIds filter is provided)',
          },
          {
            id: 'transactiontypes',
            name: 'transactiontypes',
            description: 'Comma-separated list of transaction type IDs considered for this row',
          },
          {
            id: 'total_incoming_volume',
            name: 'total_incoming_volume',
            description: 'Total incoming transaction volume in the selected currency and period',
          },
          {
            id: 'total_incoming_frequency',
            name: 'total_incoming_frequency',
            description: 'Total count of incoming transactions in the selected currency and period',
          },
          {
            id: 'total_outgoing_volume',
            name: 'total_outgoing_volume',
            description: 'Total outgoing transaction volume in the selected currency and period',
          },
          {
            id: 'total_outgoing_frequency',
            name: 'total_outgoing_frequency',
            description: 'Total count of outgoing transactions in the selected currency and period',
          },
          {
            id: 'total_processing_volume',
            name: 'total_processing_volume',
            description: 'Total processing transaction volume in the selected currency and period',
          },
          {
            id: 'total_processing_frequency',
            name: 'total_processing_frequency',
            description: 'Total count of processing transactions in the selected currency and period',
          },
          {
            id: 'total_volume',
            name: 'total_volume',
            description: 'Sum of total_incoming_volume and total_outgoing_volume',
          },
          {
            id: 'total_frequency',
            name: 'total_frequency',
            description: 'Sum of total_incoming_frequency and total_outgoing_frequency',
          },
          {
            id: 'net_volume',
            name: 'net_volume',
            description: 'Difference between total_incoming_volume and total_outgoing_volume',
            isRequired: true,
          },
          {
            id: 'net_frequency',
            name: 'net_frequency',
            description: 'Difference between total_incoming_frequency and total_outgoing_frequency',
          },
          {
            id: 'min_daily_incoming_volume',
            name: 'min_daily_incoming_volume',
            description: 'Minimum daily incoming volume recorded in the period',
          },
          {
            id: 'min_daily_outgoing_volume',
            name: 'min_daily_outgoing_volume',
            description: 'Minimum daily outgoing volume recorded in the period',
          },
          {
            id: 'min_daily_processing_volume',
            name: 'min_daily_processing_volume',
            description: 'Minimum daily processing volume recorded in the period',
          },
          {
            id: 'avg_daily_incoming_volume',
            name: 'avg_daily_incoming_volume',
            description: 'Average daily incoming volume across the period',
          },
          {
            id: 'avg_daily_outgoing_volume',
            name: 'avg_daily_outgoing_volume',
            description: 'Average daily outgoing volume across the period',
          },
          {
            id: 'avg_daily_processing_volume',
            name: 'avg_daily_processing_volume',
            description: 'Average daily processing volume across the period',
          },
          {
            id: 'max_daily_incoming_volume',
            name: 'max_daily_incoming_volume',
            description: 'Maximum daily incoming volume recorded in the period',
          },
          {
            id: 'max_daily_outgoing_volume',
            name: 'max_daily_outgoing_volume',
            description: 'Maximum daily outgoing volume recorded in the period',
          },
          {
            id: 'max_daily_processing_volume',
            name: 'max_daily_processing_volume',
            description: 'Maximum daily processing volume recorded in the period',
          },
          {
            id: 'nb_days_incoming',
            name: 'nb_days_incoming',
            description: 'Number of days with at least one incoming transaction in the period',
          },
          {
            id: 'nb_days_outgoing',
            name: 'nb_days_outgoing',
            description: 'Number of days with at least one outgoing transaction in the period',
          },
        ],
        rows: [
          {
            id: 'one-row-per-entity',
            name: 'One row per entity',
            description: 'Each row represents one entity that had at least one transaction of the specified type(s) within the selected period and currency.',
          },
        ],
      },
    ],
  },

  [reportTypes.ENTITY_OVERVIEW]: {
    detailedDescription: entityOverviewDescription,
    outputSheets: [
      {
        id: 'entities',
        name: 'Entities',
        columns: [
          {
            id: 'entityid',
            name: 'entityid',
            description: 'The unique identifier of the entity',
          },
          {
            id: 'entitytype',
            name: 'entitytype',
            description: 'The entity type (Individual, Business, Account, Product)',
          },
          {
            id: 'createdat',
            name: 'createdat',
            description: 'The date and time the entity was created in the system',
            isRequired: true,
          },
          {
            id: 'identifier',
            name: 'identifier',
            description: 'The primary identifier of the entity (SSN, business registration number, IBAN, etc.)',
          },
          {
            id: 'name',
            name: 'name',
            description: 'The display name of the entity',
          },
          {
            id: 'country',
            name: 'country',
            description: 'The country associated with the entity',
          },
          {
            id: 'status',
            name: 'status',
            description: 'The current status of the entity in the system',
          },
          {
            id: 'groupnames',
            name: 'groupnames',
            description: 'Names of groups the entity belongs to at the "to" reference date',
          },
          {
            id: 'groupids',
            name: 'groupids',
            description: 'IDs of groups the entity belongs to at the "to" reference date',
          },
          {
            id: 'group_<groupName>',
            name: 'group_<groupName>',
            description: 'One column per group in groupIds (or all groups if groupIds is None). Value is 1 if the entity belongs to the group at the "to" reference date, 0 otherwise. Column name is group_<cleaned_group_name> where the name is lowercased and non-alpha chars replaced with _ (e.g. group_high_risk).',
            isDynamic: true,
          },
          {
            id: 'score',
            name: 'score',
            description: 'The risk score of the entity at the "to" reference date',
          },
          { id: 'riskclass', name: 'riskclass', description: 'The risk class name corresponding to the entity\'s score' },
          {
            id: 'riskclassid',
            name: 'riskclassid',
            description: 'The unique identifier of the risk class assigned to the entity',
          },
          {
            id: 'num_unique_ips',
            name: 'num_unique_ips',
            description: 'Total number of unique IP addresses used by the entity within the selected timeframe',
            defaultOff: true,
          },
        ],
        rows: [
          {
            id: 'one-row-per-entity',
            name: 'One row per entity',
            description: 'Each row represents one entity that fulfils the filter criteria (entity type, group membership, risk class, creation date range).',
          },
        ],
      },
      {
        id: 'riskSummary',
        name: 'Risk Summary',
        columns: [
          {
            id: 'riskclassname',
            name: 'riskclassname',
            description: 'The name of the risk class',
          },
          {
            id: 'riskclassid',
            name: 'riskclassid',
            description: 'The unique identifier of the risk class',
          },
          {
            id: 'min',
            name: 'min',
            description: 'The minimum risk score threshold for this risk class',
          },
          {
            id: 'max',
            name: 'max',
            description: 'The maximum risk score threshold for this risk class',
          },
          {
            id: 'total',
            name: 'total',
            description: 'Total number of entities in this risk class',
          },
          {
            id: '<entityType>',
            name: '<entityType>',
            description: 'Number of entities of that type in this risk class. One column per entity type in the report (e.g. individual, business, account, product — lowercased).',
            isDynamic: true,
          },
          {
            id: 'group_<groupName>',
            name: 'group_<groupName>',
            description: 'Number of entities belonging to that group in this risk class. One column per group in groupIds (e.g. group_high_risk).',
            isDynamic: true,
          },
        ],
        rows: [
          {
            id: 'one-row-per-risk-class',
            name: 'One row per risk class',
            description: 'Each row summarises the entity count for one risk class, broken down by entity type and group membership.',
          },
        ],
      },
    ],
  },

  [reportTypes.BUSINESS_USAGE_REPORT]: {
    detailedDescription: businessUsageDescription,
    outputSheets: [
      {
        id: 'business_usage',
        name: 'Business Usage',
        columns: [
          {
            id: 'individualid',
            name: 'individualid',
            description: 'The unique identifier of the individual',
          },
          {
            id: 'createdat',
            name: 'createdat',
            description: 'The date the individual was created in the system',
            isRequired: true,
          },
          {
            id: 'swedish_ssn',
            name: 'swedish_ssn',
            description: 'The Swedish social security number of the individual',
          },
          {
            id: 'firstname',
            name: 'firstname',
            description: 'The first name of the individual',
          },
          {
            id: 'lastname',
            name: 'lastname',
            description: 'The last name of the individual',
          },
          {
            id: 'nb_signals',
            name: 'nb_signals',
            description: 'Number of signals generated for the individual during the selected period',
          },
          {
            id: 'risk_class_<riskClass>',
            name: 'risk_class_<riskClass>',
            description: 'One column per risk class defined in the system. Value is 1 if the individual belongs to that risk class at the "to" date, 0 otherwise. Column name is risk_class_<cleaned_name> (lowercased, non-alpha chars replaced with _, e.g. risk_class_high_risk).',
            isDynamic: true,
          },
          {
            id: 'group_<group>',
            name: 'group_<group>',
            description: 'One column per group in groupIds. Value is 1 if the individual belongs to the group at the "to" date, 0 otherwise. Column name is group_<cleaned_name> (e.g. group_new_customer).',
            isDynamic: true,
          },
          {
            id: 'outgoing_volume_<business>',
            name: 'outgoing_volume_<business>',
            description: 'Total outgoing transaction volume from the individual to the given business in the selected currency during the from–to period.',
            isDynamic: true,
          },
          {
            id: 'outgoing_frequency_<business>',
            name: 'outgoing_frequency_<business>',
            description: 'Number of outgoing transactions from the individual to the given business in the selected currency during the from–to period.',
            isDynamic: true,
          },
          {
            id: 'incoming_volume_<business>',
            name: 'incoming_volume_<business>',
            description: 'Total incoming transaction volume to the individual from the given business in the selected currency during the from–to period.',
            isDynamic: true,
          },
          {
            id: 'incoming_frequency_<business>',
            name: 'incoming_frequency_<business>',
            description: 'Number of incoming transactions to the individual from the given business in the selected currency during the from–to period.',
            isDynamic: true,
          },
          {
            id: 'total_volume_<business>',
            name: 'total_volume_<business>',
            description: 'Sum of outgoing_volume_<business> and incoming_volume_<business>.',
            isDynamic: true,
          },
          {
            id: 'net_volume_<business>',
            name: 'net_volume_<business>',
            description: 'outgoing_volume_<business> minus incoming_volume_<business>.',
            isDynamic: true,
          },
          {
            id: 'nb_forms',
            name: 'nb_forms',
            description: 'Total number of forms submitted by the individual after the "from" date',
          },
          {
            id: 'nb_forms_<YYYY_MM_DD>',
            name: 'nb_forms_<YYYY_MM_DD>',
            description: 'One column per date in reportDates. Value is the total number of forms submitted by the individual as of that date. Column name uses underscores, e.g. nb_forms_2024_01_15.',
            isDynamic: true,
          },
        ],
        rows: [
          {
            id: 'one-row-per-individual',
            name: 'One row per individual',
            description: 'Each row represents one individual created before the "to" date who has ever transacted with at least one of the specified input businesses.',
          },
        ],
      },
    ],
  },

  [reportTypes.ACCOUNT_FIRST_INITIATOR]: {
    detailedDescription: accountFirstInitiatorDescription,
    outputSheets: [
      {
        id: 'accountsPeriodCountryBreakdown',
        name: 'accountsPeriodCountryBreakdown',
        columns: [
          {
            id: 'country',
            name: 'country',
            description: 'Standardised country name in English, plus aggregate labels such as total and unknown.',
            isRequired: true,
          },
          {
            id: '<period>',
            name: '<period>',
            description: 'One column per generated period label (full range and monthly slices). Value is count of accounts assigned to the row country for that period.',
            isDynamic: true,
            isRequired: true,
          },
        ],
        rows: [
          {
            id: 'one-row-per-country',
            name: 'One row per country',
            description: 'One row per mapped country name, plus total and unknown aggregate rows.',
          },
        ],
      },
      {
        id: 'acctsPeriodIndicatorBreakdown',
        name: 'acctsPeriodIndicatorBreakdown',
        columns: [
          {
            id: 'country_indicator',
            name: 'country_indicator',
            description: 'Country-indicator label from chosen_indicator, plus aggregate labels such as total.',
            isRequired: true,
          },
          {
            id: '<period>',
            name: '<period>',
            description: 'One column per generated period label (full range and monthly slices). Value is count of accounts whose chosen_indicator matches the row label for that period.',
            isDynamic: true,
            isRequired: true,
          },
        ],
        rows: [
          {
            id: 'one-row-per-indicator',
            name: 'One row per indicator',
            description: 'One row per chosen_indicator value and aggregate rows such as total.',
          },
        ],
      },
      {
        id: 'countryIndicatorsForAccounts',
        name: 'countryIndicatorsForAccounts',
        columns: [
          {
            id: 'accountid',
            name: 'accountid',
            description: 'The account entity ID.',
            isRequired: true,
          },
          {
            id: 'residence',
            name: 'residence',
            description: 'Residence country of connected individual datapoints (deterministically selected when several exist).',
            isRequired: true,
          },
          {
            id: 'iban_country',
            name: 'iban_country',
            description: 'Country code derived from account IBAN when available.',
            isRequired: true,
          },
          {
            id: 'top_currency',
            name: 'top_currency',
            description: 'Highest-volume currency of the account in the selected period.',
            isRequired: true,
          },
          {
            id: 'chosen_indicator',
            name: 'chosen_indicator',
            description: 'Chosen indicator with source prefix (residence_*, iban_*, currency_*) using priority residence > iban > currency.',
            isRequired: true,
          },
        ],
        rows: [
          {
            id: 'one-row-per-account',
            name: 'One row per account',
            description: 'One row per active account in the selected period, including indicator source fields and chosen indicator.',
          },
        ],
      },
    ],
  },
};
